// The anonymous counterpart of components/dashboards/WidgetFrame.tsx: same
// sandboxed-iframe/postMessage bridge, but the widget's full HTML+allowlist
// already arrived in the public share payload (no per-widget fetch), and
// tool calls proxy to the share-scoped public endpoint instead of the
// bearer-authenticated one — there is no viewing user to call "as".
import { useClient } from '@cortex/react-query';
import type { DashboardWidget } from '@cortex/types';
import { useEffect, useMemo, useRef } from 'react';

import {
  buildWidgetSrcdoc,
  parseWidgetRequest,
  unwrapCallToolResult,
  type IHostReply,
} from '../components/dashboards/widget-bridge';

export function PublicWidgetFrame({
  shareId,
  widget,
}: {
  shareId: string;
  widget: DashboardWidget;
}) {
  const client = useClient();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const srcdoc = useMemo(() => buildWidgetSrcdoc(widget.html), [widget.html]);
  const allowlist = widget.toolAllowlist;

  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      const frame = iframeRef.current;
      if (!frame?.contentWindow || e.source !== frame.contentWindow) return;
      const req = parseWidgetRequest(e.data);
      if (!req) return;
      const reply = (r: Omit<IHostReply, 'source' | 'requestId'>) =>
        frame.contentWindow?.postMessage(
          { source: 'cortex-host', requestId: req.requestId, ...r } satisfies IHostReply,
          '*',
        );

      if (req.type === 'list-tools') {
        reply({ ok: true, data: allowlist });
        return;
      }
      const allowed = allowlist.some(
        (g) => g.mcpConnectionId === req.mcpConnectionId && g.toolName === req.toolName,
      );
      if (!allowed) {
        reply({ ok: false, error: 'tool not allowed for this widget' });
        return;
      }
      try {
        const result = await client.http
          .post(
            `v1/dashboard-shares/${shareId}/tools/${req.mcpConnectionId}/${req.toolName}/call`,
            { json: { arguments: req.args ?? {} } },
          )
          .json<unknown>();
        reply(unwrapCallToolResult(result));
      } catch (err) {
        reply({ ok: false, error: err instanceof Error ? err.message : 'tool call failed' });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [client, shareId, allowlist]);

  return (
    <iframe
      ref={iframeRef}
      title={widget.title}
      sandbox="allow-scripts"
      srcDoc={srcdoc}
      style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
    />
  );
}
